import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Alert } from 'react-bootstrap';
import { UsersIcon, BoxesIcon, FileInvoiceIcon, WarningIcon } from '../common/Icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { dashboardService } from '../../services/api';
import { DashboardStats } from '../../types';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await dashboardService.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <div>Chargement...</div>;
  }

  return (
    <div>
      <h1 className="mb-4">Tableau de bord</h1>
      
      {/* Alertes cautions */}
      {stats?.upcomingCautions && stats.upcomingCautions.length > 0 && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading>Cautions à récupérer</Alert.Heading>
          <p>Vous avez {stats.upcomingCautions.length} caution(s) dont la date de notification approche :</p>
          <ul className="mb-0">
            {stats.upcomingCautions.map((caution: any) => (
              <li key={caution.id}>
                <strong>{caution.reference}</strong> - {caution.market_name} 
                (Notification: {new Date(caution.notification_date).toLocaleDateString()})
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Cartes de statistiques */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <UsersIcon size={40} className="text-primary mb-2" />
              <h3>{stats?.totalClients || 0}</h3>
              <p className="text-muted">Clients</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <BoxesIcon size={40} className="text-success mb-2" />
              <h3>{stats?.totalProducts || 0}</h3>
              <p className="text-muted">Produits/Services</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <FileInvoiceIcon size={40} className="text-info mb-2" />
              <h3>{stats?.pendingQuotes || 0}</h3>
              <p className="text-muted">Devis en cours</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <FileInvoiceIcon size={40} className="text-danger mb-2" />
              <h3>{stats?.pendingInvoices || 0}</h3>
              <p className="text-muted">Factures en attente</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <WarningIcon size={40} className="text-warning mb-2" />
              <h3>{stats?.lowStockProducts || 0}</h3>
              <p className="text-muted">Stock faible</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Graphiques */}
      <Row className="mb-4">
        <Col md={8}>
          <Card>
            <Card.Header>
              <h5>Évolution du chiffre d'affaires</h5>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats?.monthlyRevenue || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="revenue" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Header>
              <h5>Résumé financier</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <strong>Ventes totales</strong>
                <div className="text-success fs-4">
                  {new Intl.NumberFormat('fr-FR', { 
                    style: 'currency', 
                    currency: 'MAD' 
                  }).format(stats?.totalSales || 0)}
                </div>
              </div>
              <div className="mb-3">
                <strong>Achats totaux</strong>
                <div className="text-danger fs-4">
                  {new Intl.NumberFormat('fr-FR', { 
                    style: 'currency', 
                    currency: 'MAD' 
                  }).format(stats?.totalPurchases || 0)}
                </div>
              </div>
              <hr />
              <div>
                <strong>Bénéfice</strong>
                <div className="text-primary fs-4">
                  {new Intl.NumberFormat('fr-FR', { 
                    style: 'currency', 
                    currency: 'MAD' 
                  }).format((stats?.totalSales || 0) - (stats?.totalPurchases || 0))}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top produits */}
      <Row>
        <Col md={12}>
          <Card>
            <Card.Header>
              <h5>Produits les plus vendus</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Quantité vendue</th>
                    <th>Chiffre d'affaires</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.topProducts?.map((item, index) => (
                    <tr key={index}>
                      <td>{item.product.name}</td>
                      <td>{item.quantity}</td>
                      <td>
                        {new Intl.NumberFormat('fr-FR', { 
                          style: 'currency', 
                          currency: 'MAD' 
                        }).format(item.revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;